import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ButtonWrap,
  Icon,
  IconName,
  SecondaryHeader,
  useDeviceTypes,
  useDiscoverNetworkFlipBannerText,
} from "halifax";
import React, { useContext } from "react";
import { Link, NavLink, RouteComponentProps } from "react-router-dom";
import {
  CUSTOMER_PROFILE_ENTRY,
  CUSTOMER_PROFILE_VIEWED_SECTION,
  CustomerProfileEntryProperties,
} from "redmond";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  useIsTravelApp,
  useQueryParams,
  isVesperTenant,
  getConfigTenant,
} from "@capone/common";
import { trackEvent } from "../../api/v1/analytics/trackEvent";
import { ClientContext } from "../../App";
import {
  addTrackingProperties,
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { BASE_PATH_HOME, PATH_HOME } from "../../utils/paths";
import { ContactInfo } from "./components/ContactInfo";
import { FavoritesSection } from "./components/FavoritesSection";
import { FlightPreferences } from "./components/FlightPreferences";
import { HotelPreferences } from "./components/HotelPreferences";
import { PaymentMethodsSection } from "./components/PaymentMethodsSection";
import { TravelerSection } from "./components/TravelersSection";
import { CustomerProfileLandingConnectorProps } from "./container";
import "./styles.scss";
import {
  DISCOVER_NETWORK_FLIP_PILL_TEXT,
  FOOTER_CTA_TEXT,
  FOOTER_PRIMARY_TEXT,
  FOOTER_SECONDARY_TEXT,
  getTitle,
  MY_PROFILE,
  SEARCH,
  SUBTITLE,
} from "./textConstants";

export enum CustomerProfileLandingSection {
  Travelers = "travelers",
  Favorites = "favorites",
  HotelPreferences = "hotel-preferences",
  FlightPreferences = "flight-preferences",
  PaymentMethods = "payment-methods",
  ContactInfo = "contact-info",
}

const sectionsToShow: (
  isMobile: boolean,
  isWishlistExperiment: boolean,
  showDiscoverNetworkFlipPill: boolean,
  hideFavorites: boolean,
  useUpdatedIcons?: boolean,
) => {
  section: CustomerProfileLandingSection;
  label: string;
  icon: IconName;
  description?: string;
  content?: JSX.Element;
  displayTopBorder: boolean;
  pillText?: string;
}[] = (
  isMobile,
  isWishlistExperiment,
  showDiscoverNetworkFlipPill,
  hideFavorites,
  useUpdatedIcons,
) => [
  {
    section: CustomerProfileLandingSection.Travelers,
    label: "Travelers",
    icon: useUpdatedIcons ? IconName.TravelerOutline : IconName.UserOutline,
    description:
      "Manage traveler details and frequent flyer programs to speed up the booking process. ",
    content: <TravelerSection />,
    displayTopBorder: !isMobile,
  },
  ...(isWishlistExperiment && !hideFavorites
    ? [
        {
          section: CustomerProfileLandingSection.Favorites,
          label: "Favorites",
          icon: IconName.WishlistHeart,
          description: "View and manage stays you've added to favorites. ",
          content: <FavoritesSection />,
          displayTopBorder: !isMobile,
        },
      ]
    : []),
  {
    section: CustomerProfileLandingSection.HotelPreferences,
    label: "Hotel preferences",
    icon: useUpdatedIcons ? IconName.StaysOutline : IconName.HotelFunnelIcon,
    description:
      "Manage your hotel preferences to see personalized search results.",
    content: <HotelPreferences />,
    displayTopBorder: false,
  },
  {
    section: CustomerProfileLandingSection.FlightPreferences,
    label: "Flight preferences",
    icon: useUpdatedIcons
      ? IconName.DiagonalAirplaneOutline
      : IconName.FlightFunnelIcon,
    description:
      "Manage your flight preferences to see personalized search results.",
    content: <FlightPreferences />,
    displayTopBorder: false,
  },
  {
    section: CustomerProfileLandingSection.PaymentMethods,
    label: "Payment methods",
    icon: useUpdatedIcons
      ? IconName.CardOutline
      : IconName.RewardsAccountBalanceIcon,
    description:
      "Manage your saved payment methods to speed up the booking process.",
    content: <PaymentMethodsSection />,
    displayTopBorder: true,
    pillText: showDiscoverNetworkFlipPill
      ? DISCOVER_NETWORK_FLIP_PILL_TEXT
      : undefined,
  },
  {
    section: CustomerProfileLandingSection.ContactInfo,
    label: "Contact information",
    icon: useUpdatedIcons
      ? IconName.ContactInfoPhoneOutline
      : IconName.MobilePhoneOutline,
    description:
      "This information is used to notify you about any updates or changes while traveling. If you’re booking on behalf of another traveler, you can change this information on a per trip basis while checking out.",
    content: <ContactInfo />,
    displayTopBorder: true,
  },
];

export interface ICustomerProfileLandingProps
  extends CustomerProfileLandingConnectorProps,
    RouteComponentProps {}

export const CustomerProfileLanding = ({
  history,
  paymentMethods,
  rewardsAccounts,
  listPaymentMethods,
  fetchRewardsAccounts,
}: ICustomerProfileLandingProps) => {
  const { matchesMobile } = useDeviceTypes();
  const firstName =
    useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const expState = useExperiments();

  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  const isVesper = isVesperTenant(getConfigTenant());

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const params = useQueryParams();

  const { section } = params;

  const { isTravelApp } = useIsTravelApp();

  const discoverNetworkFlipBannerText = useDiscoverNetworkFlipBannerText(
    rewardsAccounts,
    paymentMethods,
  );

  const headerBreadCrumbLinks = [
    {
      onClick: () => {
        history.goBack ? history.goBack() : history.push(BASE_PATH_HOME);
      },
      label: SEARCH,
    },
    { label: MY_PROFILE },
  ];

  const handleChangeSection = (section?: CustomerProfileLandingSection) => {
    if (section) {
      history.push({
        pathname: PATH_HOME,
        search: `?section=${section}${isTravelApp ? "&isTravelApp=true" : ""}`,
      });
    } else {
      history.push({
        pathname: PATH_HOME,
        search: isTravelApp ? "?isTravelApp=true" : "",
      });
    }
  };

  const selectedSectionInfo = React.useMemo(
    () =>
      sectionsToShow(
        matchesMobile,
        isWishlistExperiment,
        discoverNetworkFlipBannerText != null,
        isVesper,
        isVesper,
      )
        .flat()
        .find((sectionToShow) => sectionToShow.section === section),
    [section, matchesMobile],
  );

  React.useEffect(() => {
    listPaymentMethods();
    fetchRewardsAccounts(undefined, true);
  }, []);

  React.useEffect(() => {
    if (section) {
      trackEvent({
        eventName: CUSTOMER_PROFILE_VIEWED_SECTION,
        properties: {
          profile_section: section,
        },
      });
    }
  }, [section]);

  React.useEffect(() => {
    if (
      !matchesMobile &&
      (!section ||
        !Object.values(CustomerProfileLandingSection).includes(
          section as CustomerProfileLandingSection,
        ))
    ) {
      handleChangeSection(CustomerProfileLandingSection.Travelers);
    } else if (
      matchesMobile &&
      section &&
      !Object.values(CustomerProfileLandingSection).includes(
        section as CustomerProfileLandingSection,
      )
    ) {
      handleChangeSection(undefined);
    }
  }, [section, matchesMobile]);

  React.useEffect(() => {
    if (expState.trackingProperties) {
      const profile_section: CustomerProfileEntryProperties["profile_section"] =
        (() => {
          switch (section) {
            case CustomerProfileLandingSection.Travelers:
            default:
              return "travelers";
            case CustomerProfileLandingSection.Favorites:
              return "favorites";
            case CustomerProfileLandingSection.HotelPreferences:
              return "hotel_preferences";
            case CustomerProfileLandingSection.FlightPreferences:
              return "flight_preferences";
            case CustomerProfileLandingSection.PaymentMethods:
              return "payment_information";
            case CustomerProfileLandingSection.ContactInfo:
              return "contact_information";
          }
        })();

      const properties: CustomerProfileEntryProperties = {
        profile_section,
      };

      trackEvent({
        eventName: CUSTOMER_PROFILE_ENTRY,
        properties: addTrackingProperties(
          expState.trackingProperties,
          properties,
        ),
      });
    }
  }, [expState.trackingProperties]);

  React.useEffect(() => {
    travelAppInterface.sendEvent({
      name: TravelAppInterfaceEventName.PAGE_LOADED,
    });
  }, []);

  return (
    <Box
      className={clsx("customer-profile-landing-root", {
        mobile: matchesMobile,
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      {!isTravelApp && (matchesMobile ? !section : true) && (
        <SecondaryHeader
          title={getTitle(firstName)}
          subtitle={SUBTITLE}
          breadCrumbLinks={matchesMobile ? undefined : headerBreadCrumbLinks}
          className="travel-wallet"
        />
      )}
      <Box
        className={clsx("customer-profile-landing-content-container", {
          "has-section": !!section,
        })}
      >
        {(matchesMobile ? !section : true) && (
          <Box
            className="customer-profile-landing-nav-tabs"
            role="tablist"
            aria-orientation="vertical"
          >
            {sectionsToShow(
              matchesMobile,
              isWishlistExperiment,
              discoverNetworkFlipBannerText != null,
              isVesper,
              isVesper,
            )
              .flat()
              .map((sectionToShow) => (
                <NavLink
                  id={`${sectionToShow.section}-tab`}
                  key={`${sectionToShow.section}-tab`}
                  role="tab"
                  aria-controls="customer-profile-landing-tabpanel"
                  aria-selected={sectionToShow.section === section}
                  to={{
                    pathname: PATH_HOME,
                    search: `?section=${sectionToShow.section}${
                      isTravelApp ? "&isTravelApp=true" : ""
                    }`,
                  }}
                  className="customer-profile-landing-nav-tab"
                  isActive={() => sectionToShow.section === section}
                  tabIndex={sectionToShow.section !== section ? -1 : undefined}
                >
                  <Icon
                    className="section-tab-icon"
                    name={sectionToShow.icon}
                  />
                  <Typography className="section-tab-label">
                    {sectionToShow.label}
                  </Typography>
                  {sectionToShow.pillText != null ? (
                    <span className="section-tab-pill">
                      {sectionToShow.pillText}
                    </span>
                  ) : undefined}

                  {(matchesMobile || !isVesper) && (
                    <Icon name={IconName.RightChevronIcon} />
                  )}
                </NavLink>
              ))}
          </Box>
        )}

        {(matchesMobile ? !!section : true) && (
          <Box
            className={clsx("customer-profile-landing-tab-content", section)}
            id="customer-profile-landing-tabpanel"
            role="tabpanel"
            aria-labelledby={`${section}-tab`}
          >
            {matchesMobile ? (
              <Box className="customer-profile-section-mobile-nav">
                {!isTravelApp ? (
                  <ButtonWrap
                    className="back-button"
                    onClick={() => handleChangeSection(undefined)}
                  >
                    <Icon
                      name={IconName.RightChevronIcon}
                      className="back-button-icon"
                    />
                  </ButtonWrap>
                ) : (
                  <div />
                )}

                <Typography className="customer-profile-section-mobile-nav-heading">
                  {selectedSectionInfo?.label}
                </Typography>

                <Box />
              </Box>
            ) : null}
            {!matchesMobile && (
              <Typography
                variant="h2"
                className="customer-profile-landing-tab-content-title"
              >
                {!isVesper && selectedSectionInfo?.icon && (
                  <Icon name={selectedSectionInfo?.icon} />
                )}
                {selectedSectionInfo?.label}
              </Typography>
            )}
            <Typography
              variant="body2"
              className={clsx(
                "customer-profile-landing-tab-content-description",
                { mobile: matchesMobile },
              )}
            >
              {selectedSectionInfo?.description}
            </Typography>
            {selectedSectionInfo?.displayTopBorder && !isVesper && <hr />}
            {selectedSectionInfo?.content}
          </Box>
        )}
      </Box>
      {!isTravelApp && (matchesMobile ? !section : true) && (
        <Box className="customer-profile-landing-footer">
          <Box className="customer-profile-landing-footer-content">
            {!isVesper && <Icon name={IconName.FlipFlopsWithBorder} />}
            <Typography
              className="footer-primary-text"
              variant={isVesper ? "h3" : undefined}
            >
              {FOOTER_PRIMARY_TEXT}
            </Typography>
            <Typography
              className="footer-secondary-text"
              variant={isVesper ? "body2" : undefined}
            >
              {FOOTER_SECONDARY_TEXT}
            </Typography>
            <Link to={BASE_PATH_HOME} className="footer-cta">
              {FOOTER_CTA_TEXT}
            </Link>
          </Box>
        </Box>
      )}
    </Box>
  );
};
